import "@/utils/validators";

import { RequestValue } from "@/api/options";
import { PersonalDataFetch } from "@/api/personalData";
import { sendUserJourney } from "@/api/userJourney";
import { payments } from "@/app/personal-data/contants";

import useBreakpoint from "@/hooks/useBreakpoint";
import useEmailAutocomplete from "@/hooks/useEmailAutocomplete";
import usePageSubmit from "@/hooks/usePageSubmit";
import useTabsValidation from "@/hooks/useTabsValidation";
import { useFormStore } from "@/store/form";
import { usePhoneBlockSaleStore } from "@/store/phoneBlock";
import { useProductStore } from "@/store/product";
import { useProvisionalLineSaleStore } from "@/store/provisionalLineSale";
import { getOnlyNumbers } from "@/utils";
import { formatPrice } from "@/utils/currency";

import {
  getMarketingInfo,
  getUuidStorageSession,
  setUuidStorageSession,
} from "@/utils/saleSessionStorage";
import {
  removeAccentAndSpaceAndSetLowerCase,
  removeAccentsAndSetUpperCase,
} from "@/utils/string";
import { GenericObject } from "@/utils/types";
import { useCallback, useEffect, useState } from "react";
import { v4 as uuidV4 } from "uuid";
import { useValidation } from "./useValidation";
import { useTagManager } from "@/hooks/useTagManager";

type PageConfig = {
  route: string;
  prevRoute: string;
  nextRoute: string;
};

const defaultPageConfig = {
  route: "/personal-data",
  prevRoute: "/",
  nextRoute: "/type-product",
};

export function useShortFlowPersonalData() {
  const { dataLayer, dataLayerOmega } = useTagManager();
  const { isOnBreakpoint: isTableOrLess } = useBreakpoint(1024);

  const [pageConfig] = useState<PageConfig>(() => {
    if (isTableOrLess) {
      return {
        ...defaultPageConfig,
        nextRoute: "/type-product",
      };
    }
    return defaultPageConfig;
  });

  useTabsValidation(pageConfig.route, pageConfig.prevRoute);

  const form = useFormStore((store) => store.value);
  const shouldAddSpecialBonus = useProductStore(
    (store) => store.shouldAddSpecialBonus
  );
  const setPayment = useProductStore((store) => store.setPayment);
  const isExpress = useProductStore((store) => store.isExpress);
  const saleId = useFormStore((store) => store.saleId);
  const [typeProductSelected] = useState(form.tipo || "MIGRACAO");

  const [cpf, setCpf] = useState(form.cpf);
  const [email, setEmail] = useState(form.email);
  const [tel, setTel] = useState(form.tel);
  const [autorizarContato, setAutorizarContato] = useState(
    form.autorizarContato
  );
  const [fullName, setFullName] = useState(form.fullName);
  const [birthDate, setBirthDate] = useState(form.birthDate);
  const [motherName, setMotherName] = useState(form.motherName);
  const [formIsValid, setFormaIsValid] = useState(false);

  const { data, isLoading } = PersonalDataFetch(cpf);

  const addData = useFormStore((store) => store.addData);
  const product = useProductStore((store) => store.item);
  const addBonusPortability = useProductStore(
    (store) => store.addBonusPortability
  );
  const removeBonusPortability = useProductStore(
    (store) => store.removeBonusPortability
  );
  const currentPrice = useProductStore((store) =>
    store.getCurrentPrice(getOnlyNumbers(form.tel).slice(0, 2))
  );
  const loyalty = useProductStore((store) => store.loyalty);
  const totalGbProduct = useProductStore((store) => store.getTotalGbProduct());
  const provisionalLine = useProvisionalLineSaleStore(
    (store) => store.provisionalLine
  );
  const isPos = useProductStore((store) => store.isPos);
  const phoneBlock = usePhoneBlockSaleStore((store) => store.phoneBlock);

  const [oldPrice, setOldPrice] = useState(0);
  const [showChangePriceAlert, setShowChangePriceAlert] = useState(false);
  const [sendDataLayer, setSendDataLayer] = useState(false);

  const { bind, isValid: IsEmailValid } = useEmailAutocomplete({
    defaultValue: form.email,
  });
  const isMultProduct = import.meta.env.VITE_CHOOSE_PRODUCT_TYPE === "1";

  const userDataNotFound =
    !data?.found &&
    cpf.replace(/([^a-z0-9]+)/gi, "").length === 11 &&
    !isLoading;

  const { pageValidationSchema, personalDataSchema } = useValidation({
    userDataNotFound,
  });

  const handleUserJourney = useCallback(
    (name: string, value: string | number | boolean) => {
      addData({
        [name]: value,
      });
      sendUserJourney({
        ...form,
        product: product!,
        loyalty,
        ...{
          [name]: value,
          passo: "DADOS_PESSOAIS",
        },
      });
    },
    [addData, form, product, loyalty]
  );

  const handleOnBlurUserJourney = useCallback(
    (name: string, value: string | number | boolean) => {
      pageValidationSchema[name]
        .isValid({
          [name]: value,
        })
        .then((isValid: boolean) => {
          if (isValid) {
            handleUserJourney(name, value);
          }
        });
    },
    [handleUserJourney, pageValidationSchema]
  );

  const handlerGBProduct = () => {
    if (isMultProduct && !isTableOrLess) {
      if (form.tipo === "PORTABILIDADE" || shouldAddSpecialBonus()) {
        addBonusPortability();
      } else {
        removeBonusPortability();
      }
    }
  };

  const submitCallback = usePageSubmit(pageConfig.nextRoute);

  const handleOnSubmit = async (formData: GenericObject) => {
    if (product) {
      await addData({ product: product });
    }

    const timeout = Math.floor(Math.random() * (4 - 1) + 1);

    setTimeout(() => {
      dataLayer({
        event: "step",
        passo: "DADOS-PESSOAIS",
        sku: removeAccentsAndSetUpperCase(product?.tim_id),
        nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
          product?.nome
        )}-${totalGbProduct}`,
        valor: formatPrice(currentPrice),
        status: "Sucesso",
      });
    }, timeout);

    handlerGBProduct();
    submitCallback({
      ...formData,
      tipo: typeProductSelected,
      possuiProvisorio: provisionalLine === 0 ? 0 : 1,
    });
  };

  useEffect(() => {
    const marketingInfo = getMarketingInfo();
    if (marketingInfo) {
      addData(JSON.parse(marketingInfo));
    }

    const uuidStorageSession = getUuidStorageSession();
    if (uuidStorageSession) {
      addData(JSON.parse(uuidStorageSession));
    }

    let payment = payments.fatura;

    if (isExpress()) {
      payment = payments.cartaodecredito;
    }

    setPayment(payment as RequestValue);
  }, [addData, isExpress, product, setPayment]);

  useEffect(() => {
    personalDataSchema
      .isValid({
        cpf,
        email,
        tel,
        autorizarContato,
        fullName,
        birthDate,
        motherName,
      })
      .then((isValid) => {
        setFormaIsValid(isValid);
      })
      .catch(() => {
        setFormaIsValid(false);
      });
  }, [
    cpf,
    email,
    tel,
    autorizarContato,
    personalDataSchema,
    fullName,
    birthDate,
    motherName,
  ]);

  useEffect(() => {
    if (sendDataLayer) return;

    dataLayerOmega({
      event: "begin_checkout",
      ecommerce: {
        transaction_id: saleId,
        currency: "BRL",
        items: [
          {
            item_name: removeAccentAndSpaceAndSetLowerCase(product?.nome),
            item_id: product?.tim_id,
            price: formatPrice(currentPrice),
            item_variant: isPos() ? "Plano TIM Black" : "Plano TIM Controle",
            quantity: 1,
          },
        ],
      },
    });
    setSendDataLayer(true);
  }, [currentPrice, dataLayerOmega, isPos, product, saleId, sendDataLayer]);

  useEffect(() => {
    addData({ tel });
  }, [tel, addData]);

  useEffect(() => {
    const isValidShowPriceAlert = oldPrice && currentPrice !== oldPrice;

    if (isValidShowPriceAlert) {
      setShowChangePriceAlert(true);
    }

    setOldPrice(currentPrice);
  }, [currentPrice, oldPrice]);

  useEffect(() => {
    const uuidForm = form.uuid;
    if (uuidForm) {
      setUuidStorageSession(uuidForm);
    }

    if (!form.uuid) {
      const uuidStorageSession = getUuidStorageSession();
      if (uuidStorageSession) {
        addData({ uuid: JSON.parse(uuidStorageSession) });
      } else {
        const uuid = uuidV4();
        if (uuid) {
          setUuidStorageSession(uuid);
        }
        addData({ uuid });
      }
    }
  }, [addData, form]);

  return {
    showChangePriceAlert,
    currentPrice,
    personalDataSchema,
    saleId,
    email,
    bind,
    IsEmailValid,
    phoneBlock,
    cpf,
    isLoading,
    userDataNotFound,
    fullName,
    birthDate,
    motherName,
    formIsValid,
    pageConfig,
    tel,
    autorizarContato,
    setEmail,
    handleOnSubmit,
    handleUserJourney,
    setAutorizarContato,
    setTel,
    handleOnBlurUserJourney,
    setMotherName,
    setCpf,
    setFullName,
    setBirthDate,
    setShowChangePriceAlert,
  };
}
